import { useEffect, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useDisclaimerTextVisibilityStore } from '@pkgs/components/hooks/stores/useDisclaimerTextVisibilityStore';
import { IMPRINT_URL_DE, IMPRINT_URL_GLOBAL } from '@pkgs/components/consts/consts';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Language } from '@ev-tech/search-modules-api';

import { useAppContext } from '../../hooks/useAppContext';

import styles from './DisclaimerText.module.css';

const disclaimerTextVariants = cva(styles.disclaimerText, {
  variants: {
    isMapView: {
      true: styles.mapView,
      false: null,
    },
  },
});

type DisclaimerTextProps = {
  isMapView: NonNullable<VariantProps<typeof disclaimerTextVariants>['isMapView']>;
};

export const DisclaimerText = ({ isMapView }: DisclaimerTextProps) => {
  const {
    t,
    TransComponent,
    options: { language },
  } = useAppContext();
  // the Trans component creates a hydration error, so we need to use a state to ensure it is only rendered on the client
  const [isMounted, setIsMounted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { setIsDisclaimerTextBelowFold } = useDisclaimerTextVisibilityStore();

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        if (rect.top > 250) {
          setIsDisclaimerTextBelowFold(true);
        } else if (rect.top <= 250) {
          setIsDisclaimerTextBelowFold(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setIsDisclaimerTextBelowFold]);

  const imprintUrl = language === Language.de ? IMPRINT_URL_DE : IMPRINT_URL_GLOBAL;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div ref={ref}>
      <Copy
        className={disclaimerTextVariants({ isMapView })}
        size="m"
        text={
          isMounted ? (
            <TransComponent
              i18nKey="search.searchResult.listView.disclaimer"
              t={t as TFunction}
              components={[
                <a
                  className={styles.disclaimerTextLink}
                  key={0}
                  data-testid="search-components_disclaimer-text_link"
                  href={imprintUrl}
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            />
          ) : (
            // Render a placeholder during SSR to maintain layout
            '\u00A0' // Non-breaking space to maintain height
          )
        }
      />
    </div>
  );
};
